import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { StackColumn, Grid } from '@dhl-ui/components'
import { createVerificationCodeValidationSchema, VerificationCodeInput } from '@dhl/inputs'

import { TrackTraceIllustration } from '@dhl-ui/illustrations'
import { useGetProfile } from '~/profile/model/profile/hooks.js'
import { useVerifyProfileMobileNumber } from '~/profile/model/settings/mobileNumber/hooks.js'
import { MobileNumberForm } from '~/profile/view/settings/mobileNumber/MobileNumberForm.js'
import type { FormValues } from '~/profile/view/settings/mobileNumber/VerifyMobileNumberPage.js'
import { StepDescription } from '~/setup/view/components/StepDescription.js'
import { StepForm } from '~/setup/view/components/StepForm.js'
import { useWizardContext, useSetTitle } from '~/setup/model/context.js'

export const PhoneNumberVerification: FC = () => {
  const { t } = useTranslation()
  const { onStepComplete } = useWizardContext()

  useSetTitle(t('portal.profileSetup.phoneNumber.pageTitle'))
  const { data: profile } = useGetProfile()
  const verifyProfileMobileNumber = useVerifyProfileMobileNumber()

  const validationSchema: Yup.Schema<Pick<FormValues, 'verificationToken'>> = Yup.object({
    verificationToken: createVerificationCodeValidationSchema(t),
  })

  const initialValues: FormValues = {
    mobileNumber: profile?.mobilePhone?.number ?? '',
    verificationToken: '',
  }

  const handleSubmit = async (values: FormValues) => {
    await verifyProfileMobileNumber(values)

    onStepComplete()
  }

  return (
    <StackColumn gap={3}>
      <StepDescription
        slotProps={{ illustration: { variant: TrackTraceIllustration } }}
        title={t('portal.profileSetup.phoneNumber.stepTitle')}
        description={t('portal.profileSetup.phoneNumber.stepDescription')}
      />

      <StepForm initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        <MobileNumberForm disabled />

        <Grid container direction="column" spacing={2}>
          <Grid>
            <VerificationCodeInput autoSubmit={false} name="verificationToken" />
          </Grid>
        </Grid>
      </StepForm>
    </StackColumn>
  )
}
